import React, {useState, useRef, useEffect, useMemo} from 'react';
import {eachMinuteOfInterval, format} from 'date-fns';
import {Button} from '@molecules';
import {cn} from '@utils';

type TimeStamp = string; //`${number}:${number}`;
interface TimePickerProps {
  value?: TimeStamp;
  onChange?: (date: TimeStamp | null) => void;
  hasError?: boolean;
  presetTransformer?: (value: {id: TimeStamp; name: string}) => {
    id: TimeStamp;
    name: string;
    disabled?: boolean;
    hidden?: boolean;
  };
}

const STEP_IN_MINUTES = 30;

export const TimeSlotsTimePicker = ({
  value,
  onChange,
  hasError,
  presetTransformer = (o) => ({
    ...o,
    hidden: o.id === '00:00' || o.id === '23:59',
  }),
}: TimePickerProps) => {
  const [initialHours, initialMinutes] = (value || '00:00').split(':');
  const [selectedTime, setSelectedTime] = useState<TimeStamp | null>(value || null);
  const [hours, setHours] = useState(initialHours);
  const [minutes, setMinutes] = useState(initialMinutes);
  const [isOpen, setIsOpen] = useState(false);
  const minutesInputRef = useRef<HTMLInputElement>(null);
  const hoursInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  if (value !== selectedTime) {
    setSelectedTime(value || null);
  }

  const initialPresetTimes = useMemo(() => {
    const r: Array<{
      id: TimeStamp;
      name: string;
    }> = eachMinuteOfInterval(
      {start: new Date(0, 0, 0, 0, 0), end: new Date(0, 0, 0, 23, 30)},
      {step: STEP_IN_MINUTES},
    ).map((date) => ({
      id: format(date, 'HH:mm') as TimeStamp,
      name: format(date, 'HH:mm'),
    }));

    r.push({id: '23:59' as TimeStamp, name: '23:59'}); // last option
    return r;
  }, []);

  const presetTimes = useMemo(() => {
    return initialPresetTimes.map(presetTransformer).filter((o) => !o.hidden);
  }, [initialPresetTimes, presetTransformer]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateValue = (newValue: TimeStamp | null) => {
    setSelectedTime(newValue);

    onChange?.(newValue);
  };

  const handlePresetClick = (presetTime: TimeStamp) => {
    updateValue(presetTime);
    const [newHours, newMinutes] = presetTime.split(':').map(Number);
    setHours(newHours !== null ? String(newHours).padStart(2, '0') : '');
    setMinutes(newMinutes !== null ? String(newMinutes).padStart(2, '0') : '');

    setIsOpen(false);
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 2) {
      setHours(value);

      // Auto-focus minutes input after 2 characters
      if (value.length === 2) {
        minutesInputRef.current?.focus();
        minutesInputRef.current?.select();
      }

      // Update time if valid
      if (value.length > 0 && minutes.length > 0) {
        updateTimeFromInputs(value, minutes);
      }
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 2) {
      setMinutes(value);

      // Update time if valid
      if (value.length > 0 && hours.length > 0) {
        updateTimeFromInputs(hours, value);
      }
    }
  };

  const updateTimeFromInputs = (hoursValue: string, minutesValue: string) => {
    const h = parseInt(hoursValue);
    const m = parseInt(minutesValue);

    if (!isNaN(h) && !isNaN(m) && h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      const newDate = new Date();
      newDate.setHours(h, m, 0, 0);
      const next = format(newDate, 'HH:mm') as TimeStamp;

      updateValue(next);
    }
  };

  const handleHoursBlur = () => {
    // Use setTimeout to ensure state has updated
    setTimeout(() => {
      if (hoursInputRef.current && hoursInputRef.current.value.length === 1) {
        const paddedValue = hoursInputRef.current.value.padStart(2, '0');
        setHours(paddedValue);
      }
    }, 0);
  };

  const handleMinutesBlur = () => {
    // Use setTimeout to ensure state has updated
    setTimeout(() => {
      if (minutesInputRef.current && minutesInputRef.current.value.length === 1) {
        const paddedValue = minutesInputRef.current.value.padStart(2, '0');
        setMinutes(paddedValue);
      }
    }, 0);
  };

  useEffect(() => {
    const scrollToPreset = () => {
      const targetId = selectedTime || null;
      if (!targetId) return;

      containerRef.current?.querySelector(`[data-preset-id="${targetId}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    };

    if (isOpen) {
      scrollToPreset();
    }
  }, [selectedTime, isOpen]);

  const handleInputFocus = () => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      const targetId = selectedTime ? `time-preset-${selectedTime}` : null;
      if (!targetId) return;

      containerRef.current?.querySelector(`[id="${targetId}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  };

  const handleArrowPress = (direction: 'up' | 'down', type: 'hours' | 'minutes', bigIncrement?: boolean) => {
    const stepSize = bigIncrement ? 10 : 1;
    const modifier = direction === 'up' ? 1 : -1;
    const setStateFn = type === 'hours' ? setHours : setMinutes;
    const current = Number(type === 'hours' ? hours : minutes) || 0;

    const newValue = normalizeValue(current + stepSize * modifier, type);
    const next = String(newValue).padStart(2, '0');
    const other = type === 'hours' ? String(minutes).padStart(2, '0') : String(hours).padStart(2, '0');

    const nextTimeStamp = (type === 'hours' ? `${next}:${other}` : `${other}:${next}`) as TimeStamp;

    setStateFn(next);
    updateValue(nextTimeStamp);
  };

  useEffect(() => {
    const handleFocusChange = () => {
      setTimeout(() => {
        if (isOpen) {
          const active = document.activeElement;

          if (
            active === hoursInputRef.current ||
            active === minutesInputRef.current ||
            dropdownRef.current?.contains(active as Node)
          )
            return;

          setIsOpen(false);
        }
      }, 0);
    };

    document.addEventListener('focusout', handleFocusChange);
    return () => document.removeEventListener('focusout', handleFocusChange);
  }, [isOpen]);

  return (
    <div className="space-y-4">
      <div
        className="relative"
        ref={containerRef}>
        <div
          className={cn(
            'flex items-center gap-1 p-2 border rounded-lg bg-white hover:border-mapiq-black-200 transition-colors cursor-text',
            {'border-functional-red text-functional-red': hasError},
          )}>
          <input
            ref={hoursInputRef}
            type="text"
            value={hours}
            onChange={handleHoursChange}
            onFocus={handleInputFocus}
            onBlur={handleHoursBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                e.preventDefault();
                setIsOpen(false);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                handleArrowPress('up', 'hours', e.shiftKey);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                handleArrowPress('down', 'hours', e.shiftKey);
              }
            }}
            placeholder="HH"
            maxLength={2}
            className="w-8 text-center outline-none bg-transparent border-none shadow-none"
          />
          <span>:</span>
          <input
            ref={minutesInputRef}
            type="text"
            value={minutes}
            onChange={handleMinutesChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                e.preventDefault();
                setIsOpen(false);
              } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
                hoursInputRef.current?.focus();
              }

              if (e.key === 'ArrowUp') {
                e.preventDefault();
                handleArrowPress('up', 'minutes', e.shiftKey);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                handleArrowPress('down', 'minutes', e.shiftKey);
              }
            }}
            onFocus={handleInputFocus}
            onBlur={handleMinutesBlur}
            placeholder="MM"
            maxLength={2}
            className="w-8 text-center outline-none bg-transparent border-none shadow-none"
          />
        </div>

        {isOpen && presetTimes.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-60">
            <div className="grid grid-cols-1 gap-2">
              {presetTimes.map((preset) => {
                const selectionIsPreset = selectedTime === preset.id;

                return (
                  <Button
                    key={preset.id}
                    data-preset-id={preset.id}
                    button={selectionIsPreset ? undefined : 'tertiary'}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePresetClick(preset.id);
                    }}
                    size="small"
                    className={cn('h-9', {})}>
                    {preset.name}
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const normalizeValue = (value: number, type: 'hours' | 'minutes') => {
  const max = type === 'hours' ? 24 : 60;
  return value >= max ? value - max : value < 0 ? value + max : value;
};
